import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  canonicalizeCountryName,
  getFeatureCommonName,
} from "@/utils/countries/africa";
import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { useMapData, type MapRegion } from "@/hooks/dashboard/use-map-data";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

interface MapWidgetProps {
  data: {
    type: "map";
    title: string;
    countries: Record<
      string,
      { values: Record<string, unknown> | Array<Record<string, unknown>>; colorValue?: string }
    >;
    meta: any;
    empty: boolean;
  };
  widget: any;
}

function MapDataTable({ data, formatValue }: { data: Record<string, unknown>[]; formatValue: (v: any) => string }) {
  const headers = new Set<string>();
  data.forEach(row => Object.keys(row).forEach(key => headers.add(key)));
  const headerArray = Array.from(headers);

  const columnHelper = createColumnHelper<Record<string, unknown>>();
  const columns: ColumnDef<Record<string, unknown>, any>[] = headerArray.map(header =>
    columnHelper.accessor(header, {
      header: ({ column }) => {
        const isSorted = column.getIsSorted();
        return (
          <button
            className="flex items-center gap-1 hover:bg-gray-100 px-1 py-1 rounded"
            onClick={() => column.toggleSorting()}
          >
            {header}
            {isSorted === "asc" ? (
              <ArrowUp className="h-3 w-3" />
            ) : isSorted === "desc" ? (
              <ArrowDown className="h-3 w-3" />
            ) : (
              <ArrowUpDown className="h-3 w-3 opacity-50" />
            )}
          </button>
        );
      },
      cell: ({ getValue }) => {
        const formatted = formatValue(getValue());
        return <span title={formatted} className="max-w-[120px] sm:max-w-[200px] truncate block text-xs sm:text-sm">{formatted}</span>;
      },
    })
  );

  const [sorting, setSorting] = useState<SortingState>([{ id: headerArray[0], desc: false }]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  return (
    <div className="overflow-auto -mx-1 sm:mx-0">
      <Table>
        <TableHeader className="sticky top-0 bg-white border-b">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} className="text-xs sm:text-sm px-2 sm:px-4">
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row, rowIndex) => (
              <TableRow
                key={row.id}
                className={rowIndex % 2 === 0 ? "bg-gray-50" : "bg-white"}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="px-2 sm:px-4 py-2">
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-24 text-center text-xs sm:text-sm"
              >
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export default function MapWidget({ data, widget }: MapWidgetProps) {

  const { t } = useTranslation();
  const [selectedCountryKey, setSelectedCountryKey] = useState<string | null>(
    null
  );
  const [selectedCountryName, setSelectedCountryName] = useState<string | null>(
    null
  );
  // const contentRef = React.useRef<HTMLDivElement | null>(null);
  const dialogRef = React.useRef<HTMLDivElement | null>(null);
  const [_footerPx, setFooterPx] = useState<number>(0);
  const [_containerPx, setContainerPx] = useState<number>(0);

  const recalcSizes = React.useCallback(() => {
    const d = dialogRef.current;
    if (!d) return;
    const h = d.clientHeight;
    if (h > 0) {
      setContainerPx(h);
      setFooterPx(Math.max(48, Math.floor(h * 0.15)));
    }
  }, []);

  React.useEffect(() => {
    recalcSizes();
    const d = dialogRef.current;
    if (!d) return;
    const ro = new ResizeObserver(() => recalcSizes());
    ro.observe(d);
    const onResize = () => recalcSizes();
    window.addEventListener("resize", onResize);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, [recalcSizes]);
  const mapOptions = widget?.config?.options?.map || {};
  const region = mapOptions?.region || "africa";
  const appearance = mapOptions?.appearance || {};
  const skipYear = appearance?.skipYear !== false; // default true
  const coloringMode = appearance?.coloringMode || "solid";
  const solidColor = appearance?.solidColor || "#012473";
  const disabledColor = "#909090";
  const borderEnabled = appearance?.border?.enabled || false;
  const borderColor = appearance?.border?.color || "#ffffff";
  const showCountryName = !!appearance?.showCountryName;
  const showCountryFlag = !!appearance?.showCountryFlag;
  const footerImage = appearance?.footerImage as string | undefined;
  const showSubtitle = !!appearance?.showSubtitle;
  const subtitle = appearance?.subtitle || "";

  const formatNumber = (n: number, skipCommaForYear: boolean = false): string => {
    if (skipCommaForYear) {
      return String(n);
    }
    return n.toLocaleString();
  };
  const isValidYear = (n: number): boolean => {
    return n >= 1000 && n <= 9999 && Number.isInteger(n);
  };
  const isNumericString = (s: string) => {
    if (typeof s !== "string") return false;
    if (s.trim() === "") return false;
    const n = Number(s);
    return Number.isFinite(n);
  };
  const isValidDateString = (s: string) => {
    if (typeof s !== "string") return false;
    const t = Date.parse(s);
    return !isNaN(t);
  };
  const formatDateHuman = (input: string | Date) => {
    const d = input instanceof Date ? input : new Date(input);
    if (isNaN(d.getTime())) return String(input);
    const hasTime = typeof input === "string" && /T|\d{1,2}:\d{2}/.test(input);
    return hasTime
      ? d.toLocaleString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      : d.toLocaleDateString("en-GB", {
          day: "numeric",
          month: "long",
          year: "numeric",
        });
  };
  const formatValue = (v: any): string => {
    if (v === null || v === undefined) return "-";
    if (typeof v === "number") {
      const shouldSkipComma = skipYear && isValidYear(v);
      return formatNumber(v, shouldSkipComma);
    }
    if (typeof v === "boolean") return v ? t('processManagement.yes') : t('processManagement.no');
    if (v instanceof Date) return formatDateHuman(v);
    if (Array.isArray(v)) return v.map((x) => formatValue(x)).join(", ");
    if (typeof v === "object") return JSON.stringify(v);
    const s = String(v);
    if (isNumericString(s)) {
      const num = Number(s);
      const shouldSkipComma = skipYear && isValidYear(num);
      return formatNumber(num, shouldSkipComma);
    }
    if (isValidDateString(s)) return formatDateHuman(s);
    return s;
  };

  const { data: geoData, loading: geoDataLoading, error: geoDataError } = useMapData(region as MapRegion);

  const countryAliases: Record<string, string> = {
    "Guinea Bissau": "Guinea-Bissau",
    "Republic of Congo": "Congo",
    Somaliland: "Somalia",
  };

  // Countries that need to use alpha-2 codes to avoid ambiguous matches
  const countryAlpha2Codes: Record<string, string> = {
    "niger": "NE", // Niger (not Nigeria)
  };

  const [flags, setFlags] = useState<
    Record<string, { svg?: string; png?: string } | null>
  >({});

  const getFlag = async (canonicalName: string) => {
    if (canonicalName in flags) return;

    try {
      // Use alpha-2 code for countries that have ambiguous names
      const alpha2Code = countryAlpha2Codes[canonicalName];
      if (alpha2Code) {
        const response = await fetch(
          `https://restcountries.com/v3.1/alpha/${alpha2Code}`
        );
        if (response.ok) {
          const data = await response.json();
          if (data && data.flags) {
            setFlags((prev) => ({ ...prev, [canonicalName]: data.flags }));
            return;
          }
        }
      }

      const apiName = countryAliases[canonicalName] || canonicalName.replace(/\b\w/g, l => l.toUpperCase());
      const response = await fetch(
        `https://restcountries.com/v3.1/name/${encodeURIComponent(apiName)}`
      );
      if (!response.ok) {
        setFlags((prev) => ({ ...prev, [canonicalName]: null }));
        return;
      }
      const data = await response.json();
      if (data && Array.isArray(data) && data.length > 0) {
        // Find exact match by comparing country names (case-insensitive)
        // This prevents "Niger" from matching "Nigeria"
        const exactMatch = data.find(
          (country: any) =>
            country.name?.common?.toLowerCase() === apiName.toLowerCase() ||
            country.name?.official?.toLowerCase() === apiName.toLowerCase()
        );
        if (exactMatch) {
          setFlags((prev) => ({ ...prev, [canonicalName]: exactMatch.flags }));
        } else if (data.length === 1) {
          // If only one result, use it (for countries without ambiguous names)
          setFlags((prev) => ({ ...prev, [canonicalName]: data[0].flags }));
        } else {
          setFlags((prev) => ({ ...prev, [canonicalName]: null }));
        }
      } else {
        setFlags((prev) => ({ ...prev, [canonicalName]: null }));
      }
    } catch (error) {
      console.error("Failed to fetch flag for", canonicalName, error);
      setFlags((prev) => ({ ...prev, [canonicalName]: null }));
    }
  };

  React.useEffect(() => {
    if (selectedCountryKey && showCountryFlag) {
      const flag = flags[selectedCountryKey];
      if (flag === undefined) {
        getFlag(selectedCountryKey);
      }
    }
  }, [selectedCountryKey, showCountryFlag]);


  const handleCountryClick = (key: string, displayName: string) => {
    setSelectedCountryKey(key);
    setSelectedCountryName(displayName);
  };

  const closeModal = () => {
    setSelectedCountryKey(null);
    setSelectedCountryName(null);
  };

  if (geoDataLoading) {
    return (
      <div className="relative h-full w-full flex items-center justify-center">
        <div className="text-gray-500">{t('forms.loadingMapData')}</div>
      </div>
    );
  }

  if (geoDataError) {
    return (
      <div className="relative h-full w-full flex items-center justify-center">
        <div className="text-red-500">{t('forms.errorLoadingMapData', { error: geoDataError })}</div>
      </div>
    );
  }

  if (!geoData) {
    return (
      <div className="relative h-full w-full flex items-center justify-center">
        <div className="text-gray-500">{t('forms.mapDataNotAvailable')}</div>
      </div>
    );
  }

  return (
    <div className="relative h-full w-full flex flex-col">
      {showSubtitle && subtitle && (
        <div className="flex-none px-2 py-1 text-xs sm:text-sm text-gray-700 font-medium border-b text-center">
          {subtitle}
        </div>
      )}
      <div className="flex-1 relative">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ scale: region === "world" ? 100 : 600, center: region === "world" ? [0, 0] : [20, 0] }}
        style={{ width: "100%", height: "100%" }}
      >
        <ZoomableGroup center={[0, 0]} maxZoom={40} zoom={1}>
          <Geographies geography={geoData}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const featureNameRaw = getFeatureCommonName(geo.properties, region);
              const key = canonicalizeCountryName(featureNameRaw);
              const entry = data.countries[key];
              const hasData = !!entry;
              const hasAnyValue = hasData && (() => {
                const values = entry.values;
                if (Array.isArray(values)) {
                  return values.some(row =>
                    Object.values(row).some(v => v !== null && v !== undefined && String(v) !== "")
                  );
                } else {
                  return Object.values(values || {}).some(
                    (v) => v !== null && v !== undefined && String(v) !== ""
                  );
                }
              })();
              const clickable = hasAnyValue;
              const colorValue = entry?.colorValue;

              const fillColor = hasData
                ? (() => {
                    if (coloringMode === "options") {
                      return colorValue || disabledColor;
                    } else {
                      return solidColor;
                    }
                  })()
                : disabledColor;

              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  onClick={
                    clickable
                      ? () => handleCountryClick(key, featureNameRaw)
                      : undefined
                  }
                  style={{
                    default: {
                      fill: fillColor,
                      outline: "none",
                      stroke: borderEnabled ? borderColor : "#fff",
                      strokeWidth: borderEnabled ? 0.8 : 0.3,
                    },
                    hover: {
                      fill: clickable ? "#1E3A8A" : fillColor,
                      outline: "none",
                      cursor: clickable ? "pointer" : "default",
                    },
                    pressed: {
                      fill: clickable ? "#1D4ED8" : fillColor,
                      outline: "none",
                    },
                  }}
                />
              );
            })
          }
        </Geographies>
        </ZoomableGroup>
      </ComposableMap>

      {coloringMode === "options" && appearance?.optionColors && (
        <div className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-white bg-opacity-90 sm:bg-opacity-80 p-1.5 sm:p-2 rounded shadow z-10 max-w-[calc(100%-0.5rem)] sm:max-w-none">
          {Object.entries(appearance.optionColors).map(([value, color]) => (
            <div key={value} className="flex items-center gap-1 text-[10px] sm:text-xs mb-0.5 sm:mb-1">
              <div
                style={{ backgroundColor: color as string, width: '10px', height: '10px', border: '1px solid #ccc' }}
                className="rounded flex-shrink-0"
              ></div>
              <span className="truncate max-w-[100px] sm:max-w-none">{value}</span>
            </div>
          ))}
          <div className="flex items-center gap-1 text-[10px] sm:text-xs mb-0.5 sm:mb-1">
            <div
              style={{ backgroundColor: disabledColor, width: '10px', height: '10px', border: '1px solid #ccc' }}
              className="rounded flex-shrink-0"
            ></div>
            <span>No Data</span>
          </div>
        </div>
      )}

      <Dialog
        open={!!selectedCountryKey}
        onOpenChange={(o) => {
          if (!o) closeModal();
        }}
      >
        <DialogContent
          ref={dialogRef as any}
          className="flex flex-col overflow-hidden w-[95vw] sm:w-[90vw] md:min-w-[600px] lg:min-w-[750px] max-w-[750px] min-h-[50vh] sm:min-h-[320px] max-h-[90vh]"
        >
          {/* Header */}
          <DialogHeader className="flex-none">
            {showCountryName && (
              <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
                {showCountryFlag &&
                  (() => {
                    const f = flags[selectedCountryKey!];
                    const src = f ? f.svg || f.png : undefined;
                    return src ? (
                      <img
                        src={src}
                        alt="flag"
                        className="h-4 w-6 sm:h-5 sm:w-7 object-cover rounded-sm flex-shrink-0"
                      />
                    ) : null;
                  })()}
                <span className="truncate">{selectedCountryName || selectedCountryKey || ""}</span>
              </DialogTitle>
            )}
          </DialogHeader>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-auto space-y-2 sm:space-y-3 px-1 sm:px-2">
            {selectedCountryKey && (() => {
              const values = data.countries[selectedCountryKey]?.values;
              if (Array.isArray(values)) {
                // Data table view for multiple responses
                return <MapDataTable data={values} formatValue={formatValue} />;
              } else {
                // Original key:value view for single response
                return Object.entries(values || {}).map(([label, value]) => (
                  <div key={label} className="mb-2 sm:mb-3">
                    <div className="text-xs sm:text-sm font-semibold text-gray-800">
                      {label}:
                    </div>
                    <div className="text-xs sm:text-sm text-gray-700 break-words">
                      {formatValue(value)}
                    </div>
                  </div>
                ));
              }
            })()}
          </div>

          {/* Footer with Image (25% of modal height) */}
          {footerImage && (
            <div className="flex-none border-t pt-2 w-full relative">
              <div className="relative w-full" style={{ minHeight: "80px", maxHeight: "25%" }}>
                <img
                  src={`/api/uploads/${footerImage}`}
                  alt="footer"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
